"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Search, Star, Code as Code2, MessageSquare, Share2, FileText, Layers, Globe as Globe2, Wrench, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const localServices = [{
  id: "gbp-opt",
  title: "Google Business Profile Optimization",
  icon: MapPin,
  badge: "Core Service",
  description: "Complete optimization of your Google Business Profile to claim maximum real estate in Google Map Packs and localized search results.",
  deliverables: ["GBP setup, audit & category targeting", "Geotagged image & media strategy", "Weekly post & product updates"]
}, {
  id: "citation-building",
  title: "Local Citation Building & Audits",
  icon: Building2,
  badge: "Authority",
  description: "Audit and cleanup of NAP (Name, Address, Phone) consistency across premier UK directories, industry hubs, and mapping platforms.",
  deliverables: ["Duplicate citation removal & cleanup", "High-DA localized directory submissions", "Aggregator syndication management"]
}, {
  id: "keyword-research",
  title: "Geo-Targeted Keyword Strategy",
  icon: Search,
  badge: "Intent Targeting",
  description: "In-depth research identifying high-converting local search terms, neighborhood terms, and intent-driven local service queries.",
  deliverables: ["Search volume & intent mapping", "Competitor map-pack gap analysis", "Hyper-local keyword clustering"]
}, {
  id: "review-management",
  title: "Review Strategy & Management",
  icon: Star,
  badge: "Reputation",
  description: "Ethical customer review acquisition workflows and response strategies designed to build local trust and elevate map pack signals.",
  deliverables: ["Review generation campaign setup", "Custom response strategy frameworks", "Sentiment monitoring & reporting"]
}, {
  id: "local-schema",
  title: "Local Schema & Structured Data",
  icon: Code2,
  badge: "Technical",
  description: "Advanced LocalBusiness JSON-LD markup integration specifying exact geo-coordinates, operating hours, service areas, and price ranges.",
  deliverables: ["Multi-location LocalBusiness schema", "HasMap & GeoCoordinates integration", "Schema validation & error monitoring"]
}, {
  id: "onpage-geo",
  title: "Localized On-Page Optimization",
  icon: FileText,
  badge: "Conversion",
  description: "Crafting hyper-relevant city landing pages and location pages that convert local visitors into qualified direct inquiries.",
  deliverables: ["City page structure & UX optimization", "Geo-targeted title tags & headers", "Local service page interlinking"]
}, {
  id: "hyperlocal-links",
  title: "Hyper-Local Link Acquisition",
  icon: Share2,
  badge: "Link Building",
  description: "Earning authoritative local backlinks from regional news outlets, local chamber of commerce, sponsorships, and niche blogs.",
  deliverables: ["Regional PR & outreach campaigns", "Local community sponsorship links", "Industry partner referral outreach"]
}, {
  id: "authority-content",
  title: "Local Content & Neighborhood Guides",
  icon: Layers,
  badge: "Relevance",
  description: "Developing authoritative content tailored to local events, regional regulations, and location-specific customer pain points.",
  deliverables: ["Localized FAQ hub creation", "Regional industry guide publishing", "Local intent blog strategies"]
}, {
  id: "multi-location",
  title: "Multi-Location SEO Architecture",
  icon: Globe2,
  badge: "Scalable",
  description: "Scalable local SEO structures designed for multi-branch businesses seeking unified national authority with pinpoint local visibility.",
  deliverables: ["Scalable location page hierarchy", "Centralized GBP management", "Cross-store canonical strategy"]
}, {
  id: "alt-maps",
  title: "Apple Maps & Alternative Search",
  icon: MessageSquare,
  badge: "Multi-Platform",
  description: "Optimizing your presence beyond Google across Apple Maps, Bing Places, and voice search assistants to capture all local traffic.",
  deliverables: ["Apple Business Connect setup", "Bing Places directory sync", "Voice search intent optimization"]
}, {
  id: "technical-audit",
  title: "Local Technical SEO Audits",
  icon: Wrench,
  badge: "Performance",
  description: "Diagnosing local technical bottlenecks, mobile usability issues, page speed barriers, and geo-redirect errors that harm rankings.",
  deliverables: ["Mobile-first local audit report", "Page speed & Core Web Vitals fix", "Crawl budget & indexing fixes"]
}, {
  id: "grid-tracking",
  title: "Local Rank & Geo-Grid Tracking",
  icon: BarChart3,
  badge: "Reporting",
  description: "Granular visual map-pack tracking across specific radius points, providing clear visibility into ranking growth and direct leads.",
  deliverables: ["Geo-grid ranking heatmaps", "Lead & phone call attribution", "Transparent monthly reporting"]
}];
export default function Localseo() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 mr-1 text-primary" />
            Specialized Local SEO Consultancy
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground font-default tracking-tight mb-6">
            Hands-On Local SEO Services Designed for High-Intent Lead Growth
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            I work directly with you to capture map pack dominance, eliminate local ranking barriers, and turn searchers in your target locations into paying clients. No junior account managers or generic templates.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {localServices.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.id} variants={cardVariants} data-index={index}>
                <Card className="h-full flex flex-col justify-between overflow-hidden border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 bg-card rounded-xl group">
                  {}
                  <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary-foreground/10 text-primary-foreground">
                        <Icon className="w-5 h-5" />
                      </div>
                      <Badge className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30 border-none text-2xs font-medium">
                        {service.badge}
                      </Badge>
                    </div>
                  </div>

                  {}
                  <CardHeader className="pt-6 pb-2">
                    <CardTitle className="text-xl font-bold font-default text-card-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground font-default leading-relaxed mt-2">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="py-4 flex-grow">
                    <div className="border-t border-border/60 pt-4">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                        Key Deliverables:
                      </p>
                      <ul className="space-y-2">
                        {service.deliverables.map((item, dIdx) => <li key={dIdx} className="flex items-start text-xs sm:text-sm text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mr-2.5 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 pb-6 border-t border-border/40">
                    <Button variant="ghost" size="sm" asChild className="w-full justify-between hover:bg-muted text-primary font-medium">
                      <Link to="/ContactPage">
                        <span>Consult on {service.badge}</span>
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <div className="mt-16 sm:mt-20 p-8 sm:p-10 rounded-2xl bg-muted/60 border border-border text-center max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left max-w-xl">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default mb-2">
              Need a Custom Local SEO Strategy?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-default">
              Book a free SEO consultation to discuss your specific target locations, current map pack visibility, and growth targets directly with Yassine.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button size="lg" asChild className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}